import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface EnvironmentRequest {
  id: string;
  requestNumber: string;
  shortDescription: string;
  status: string;
  approvalStatus: string;
  requestType: string;
  priority: string;
  dueDate?: string;
  createdAt: string;
  environment?: {
    id: string;
    name: string;
    cloudProviderType: string;
    environmentType: string;
    company?: {
      id: string;
      name: string;
    };
  };
  assignedTo?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  assignmentGroup?: {
    id: string;
    name: string;
  };
  approverGroup?: {
    id: string;
    name: string;
  };
  createdBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface UserGroup {
  id: string;
  groupId: string;
  group: {
    id: string;
    name: string;
  };
}

const RequestedItemsList: React.FC = () => {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [approvalFilter, setApprovalFilter] = useState<string>('all');

  // Fetch user's groups to check if they're in Onboarding-Approver
  const { data: userGroups } = useQuery({
    queryKey: ['user-groups', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const response = await api.get(`/users/${user.id}`);
      return response.data.groups as UserGroup[];
    },
    enabled: !!user?.id,
  });

  // Check if user is in Onboarding-Approver group (ONLY group members can approve)
  const isApprover = userGroups?.some(
    (ug) => ug.group.name === 'Onboarding-Approver'
  );

  // Fetch all environment requests
  const { data: requestsData, isLoading, isError } = useQuery({
    queryKey: ['environment-requests', statusFilter, approvalFilter],
    queryFn: async () => {
      let url = '/environment-requests?limit=100';
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (approvalFilter !== 'all') {
        url += `&approvalStatus=${approvalFilter}`;
      }
      const response = await api.get(url);
      return response.data;
    },
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Open':
        return 'bg-blue-100 text-blue-800';
      case 'InProgress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalBadgeColor = (status: string) => {
    switch (status) {
      case 'Requested':
        return 'bg-yellow-100 text-yellow-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading requested items...</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">Error loading requested items</div>
        </div>
      </div>
    );
  }

  const requests = requestsData?.data || [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Requested Items (RITMs)</h1>
          <p className="mt-2 text-sm text-gray-700">
            All provisioning requests and their approval status
            {isApprover && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Approver
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-4">
        <div className="flex items-center">
          <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
          <label htmlFor="status-filter" className="mr-2 text-sm text-gray-600">
            Status:
          </label>
          <select
            id="status-filter"
            className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="Open">Open</option>
            <option value="InProgress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Failed">Failed</option>
          </select>
        </div>
        <div className="flex items-center">
          <label htmlFor="approval-filter" className="mr-2 text-sm text-gray-600">
            Approval:
          </label>
          <select
            id="approval-filter"
            className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            value={approvalFilter}
            onChange={(e) => setApprovalFilter(e.target.value)}
          >
            <option value="all">All Approvals</option>
            <option value="Requested">Requested</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Request Number
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Environment
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Company
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Priority
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Approval
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Requested By
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Created
                    </th>
                    {user && (
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={user ? 10 : 9} className="px-3 py-8 text-center text-sm text-gray-500">
                        No requested items found
                      </td>
                    </tr>
                  ) : (
                    requests.map((request: EnvironmentRequest) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          <Link
                            to={`/environment-requests/${request.id}`}
                            className="flex items-center text-primary-600 hover:text-primary-900 font-medium"
                          >
                            <DocumentTextIcon className="h-4 w-4 mr-1" />
                            {request.requestNumber}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          {request.environment ? (
                            <div>
                              <div className="font-medium">{request.environment.name}</div>
                              <div className="text-xs text-gray-500">
                                {request.environment.cloudProviderType} • {request.environment.environmentType}
                              </div>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {request.environment?.company?.name || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {request.requestType}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(request.priority)}`}>
                            {request.priority}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(request.status)}`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getApprovalBadgeColor(request.approvalStatus)}`}>
                            {request.approvalStatus}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {request.createdBy ? (
                            <div>
                              {request.createdBy.firstName} {request.createdBy.lastName}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </td>
                        {user && (
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex justify-end space-x-2">
                              {isApprover && request.approvalStatus === 'Requested' ? (
                                <>
                                  <Link
                                    to={`/environment-requests/${request.id}`}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                  >
                                    <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                    Approve
                                  </Link>
                                  <Link
                                    to={`/environment-requests/${request.id}`}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  >
                                    <XCircleIcon className="h-3.5 w-3.5 mr-1" />
                                    Reject
                                  </Link>
                                </>
                              ) : (
                                <>
                                  <div 
                                    title={
                                      request.approvalStatus !== 'Requested' 
                                        ? `Request is ${request.approvalStatus}` 
                                        : "Don't have sufficient rights to approve or reject."
                                    }
                                    className="relative group inline-block"
                                  >
                                    <span className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-gray-400 cursor-not-allowed">
                                      <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                      Approve
                                    </span>
                                  </div>
                                  <div 
                                    title={
                                      request.approvalStatus !== 'Requested' 
                                        ? `Request is ${request.approvalStatus}` 
                                        : "Don't have sufficient rights to approve or reject."
                                    }
                                    className="relative group inline-block"
                                  >
                                    <span className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-gray-400 cursor-not-allowed">
                                      <XCircleIcon className="h-3.5 w-3.5 mr-1" />
                                      Reject
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestedItemsList;